import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import WebinarDialog from "@/components/WebinarDialog";

const chatScript = [
  {
    sender: "user",
    name: "Matt",
    text: "Need a volunteering event in San Jose for 50 people.",
  },
  { sender: "coraa", text: "Got it. Scanning NPOs nearby…" },
  {
    sender: "coraa",
    text: "Sent invite to 8 partners. Waiting on responses...",
  },
  { sender: "coraa", text: "5 quotes in. Analyzing cost, proximity & impact…" },
  {
    sender: "coraa",
    text: "Top pick: $30/person · 4.5 miles away · 300 meals served.  Want me to book it?",
  },
  { sender: "user", name: "Matt", text: "Book it." },
  {
    sender: "coraa",
    text: "✅ Done. Calendar blocked. Summary emailed.",
    isConfirm: true,
  },
];

const HeroSection = () => {
  const [activeSession, setActiveSession] = useState<"asia" | "us">("asia");
  const [isWebinarDialogOpen, setIsWebinarDialogOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Calculate time left for the selected session
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const targetDate = new Date(
        activeSession === "asia"
          ? "2025-06-07T11:00:00+05:30" // Asia/Kolkata time
          : "2025-06-06T11:00:00-05:00" // US Eastern time
      );

      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft();
    return () => clearInterval(timer);
  }, [activeSession]);

  // Chat animation: staggered reveal with infinite loop and human pacing
  const [chatStep, setChatStep] = useState(0);
  const [loopKey, setLoopKey] = useState(0); // for fade out/in reset
  useEffect(() => {
    if (chatStep < chatScript.length) {
      // Slow, human-like delays: longer for CORAA, shorter for Matt
      const msg = chatScript[chatStep];
      let delay = 2700;
      if (msg.sender === "user") delay = 3200;
      if (msg.isConfirm) delay = 3500;
      const timeout = setTimeout(() => setChatStep(chatStep + 1), delay);
      return () => clearTimeout(timeout);
    } else {
      // Pause, then reset
      const timeout = setTimeout(() => {
        setChatStep(0);
        setLoopKey((k) => k + 1);
      }, 2200);
      return () => clearTimeout(timeout);
    }
  }, [chatStep, loopKey]);

  // Button pulse animation
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => setPulse((p) => !p), 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="min-h-[70vh] flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[#0B0F1A] to-[#121F33] font-inter">
      {/* Animated dot grid overlay */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <svg
          width="100%"
          height="100%"
          className="absolute inset-0"
          style={{ opacity: 0.13 }}
        >
          <defs>
            <pattern
              id="dotgrid"
              x="0"
              y="0"
              width="32"
              height="32"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1.5" cy="1.5" r="1.5" fill="#2D7D46" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dotgrid)" />
        </svg>
      </div>
      <div className="container mx-auto px-6 lg:px-8 py-4 relative z-10 flex flex-col lg:flex-row items-center justify-center gap-16 lg:gap-20 max-w-7xl">
        {/* Left Side */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="flex-1 max-w-2xl text-white text-center lg:text-left lg:pl-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight drop-shadow-[0_2px_24px_rgba(45,125,70,0.13)]">
            The Moment Social Impact
            <span className="text-green-400"> Enters the Age of AI. </span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 mb-4 leading-relaxed max-w-xl break-words whitespace-pre-wrap">
            Introducing CORAA AI, the world's first AI copilot for corporate
            social impact <br />
            <br />
            <span className="bg-white text-green-600 px-3 py-1 font-semibold shadow-lg inline-flex items-center">
              <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse mr-2"></span>
              LIVE. As your co-host. Unscripted!
            </span>
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <motion.button
              onClick={() => setIsWebinarDialogOpen(true)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className={`px-6 py-3 rounded-lg text-base font-semibold shadow-md flex items-center justify-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7D46] focus-visible:ring-offset-2 bg-gradient-to-r from-[#2D7D46] to-[#3FA65B] text-white relative cursor-pointer ${
                pulse ? "shadow-[0_0_10px_2px_#2D7D46cc]" : ""
              }`}
              style={{
                boxShadow: pulse ? "0 0 10px 2px #2D7D46cc" : undefined,
              }}
              aria-label="Reserve My Spot for Webinar"
            >
              Reserve My Spot
            </motion.button>
          </div>
          <div className="text-gray-400 text-base mt-1 mb-2">
            No forms. No followups. Just impact on autopilot. This changes
            everything.
          </div>
        </motion.div>
        {/* Right Side - Chat Preview */}
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex-1 flex justify-center lg:justify-end w-full lg:pr-8"
        >
          <div className="relative w-full max-w-sm lg:max-w-md">
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 0.7, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -top-6 right-0 bg-[#121F33] border border-[#2D7D46]/15 px-3 py-1 rounded-full shadow text-xs font-semibold flex items-center gap-2 z-20"
              style={{ fontSize: "0.82rem" }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2D7D46] opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2D7D46]"></span>
              </span>
            </motion.div>
            {/* Chat Card */}
            <div className="relative bg-[#0B0F1A]/85 backdrop-blur-xl rounded-2xl border border-[#2D7D46]/20 shadow-xl overflow-hidden flex flex-col min-h-[340px] max-h-[400px]">
              {/* Timestamp */}

              {/* Chat Messages (only last 3 visible, older fade out) */}
              <div className="flex-1 px-4 pb-4 pt-1 overflow-y-auto flex flex-col gap-2">
                {chatScript
                  .slice(Math.max(0, chatStep - 3), chatStep)
                  .map((msg, i, arr) => (
                    <motion.div
                      key={i + chatStep + loopKey * 100}
                      initial={{ opacity: 0, y: 18 }}
                      animate={{
                        opacity: 0.6 + (0.4 * (i + 1)) / arr.length,
                        y: 0,
                      }}
                      transition={{ delay: i * 0.12 }}
                      className={`flex ${
                        msg.sender === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div className="flex flex-col items-start max-w-[85%]">
                        {/* Label for CORAA */}
                        {msg.sender === "coraa" && (
                          <span className="block text-xs text-[#A7FFEB] font-bold mb-1 ml-1">
                            CORAA
                          </span>
                        )}
                        <div
                          className={`rounded-xl px-3 py-2 text-sm md:text-base whitespace-pre-line shadow-sm relative ${
                            msg.sender === "user"
                              ? "bg-[#2D7D46] text-white rounded-br-sm"
                              : msg.isConfirm
                              ? "bg-black text-white rounded-bl-sm shadow-[0_0_10px_0_#2D7D46bb] border border-[#2D7D46]/40"
                              : "bg-gray-900/80 text-white rounded-bl-sm"
                          }`}
                          style={{
                            opacity:
                              i === arr.length - 1
                                ? 1
                                : 0.8 - 0.2 * (arr.length - 1 - i),
                          }}
                        >
                          {msg.sender === "user" && (
                            <span className="block text-xs text-[#b2e5c3] font-bold mb-1">
                              {msg.name}
                            </span>
                          )}
                          <span>{msg.text}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                {/* Typing dots for CORAA before each CORAA message except confirmation */}
                {chatStep < chatScript.length &&
                  chatStep > 0 &&
                  chatScript[chatStep]?.sender === "coraa" &&
                  !chatScript[chatStep]?.isConfirm && (
                    <motion.div
                      key={"typing-" + chatStep + loopKey}
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: chatStep * 0.12 }}
                      className="flex justify-start"
                    >
                      <div className="flex flex-col items-start max-w-[85%]">
                        <span className="block text-xs text-[#A7FFEB] font-bold mb-1 ml-1">
                          CORAA
                        </span>
                        <div className="rounded-xl px-3 py-2 text-sm md:text-base bg-gray-900/80 text-white rounded-bl-sm shadow-sm relative">
                          <TypingDots />
                        </div>
                      </div>
                    </motion.div>
                  )}
              </div>
              {/* Disabled input + mic icon */}
              <div className="flex items-center gap-2 px-4 pb-3 pt-1">
                <input
                  type="text"
                  disabled
                  placeholder="Message CORAA..."
                  className="w-full rounded-lg px-3 py-2 bg-[#101825]/60 text-gray-400 placeholder-gray-500 border border-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7D46] focus-visible:ring-offset-2 text-sm"
                />
                <svg
                  className="w-5 h-5 text-[#2D7D46]/90"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 18v2m0 0c-4.418 0-8-3.582-8-8m8 8c4.418 0 8-3.582 8-8m-8 8v-2m0 0V6m0 0a4 4 0 0 1 8 0v6a4 4 0 0 1-8 0z"
                  />
                </svg>
              </div>
              {/* Green radial glow behind CORAA bubbles */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2/3 h-2/3 pointer-events-none z-0">
                <div className="w-full h-full rounded-full bg-[#2D7D46]/15 blur-2xl opacity-60 animate-pulse"></div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      {isWebinarDialogOpen && (
        <WebinarDialog
          isOpen={isWebinarDialogOpen}
          onClose={() => setIsWebinarDialogOpen(false)}
        />
      )}
    </section>
  );

  // Typing dots animation
  function TypingDots() {
    return (
      <span className="inline-flex items-center gap-1">
        <span
          className="w-2 h-2 bg-[#2D7D46] rounded-full animate-bounce"
          style={{ animationDelay: "0s" }}
        />
        <span
          className="w-2 h-2 bg-[#2D7D46] rounded-full animate-bounce"
          style={{ animationDelay: "0.15s" }}
        />
        <span
          className="w-2 h-2 bg-[#2D7D46] rounded-full animate-bounce"
          style={{ animationDelay: "0.3s" }}
        />
      </span>
    );
  }
};

export default HeroSection;
